import type { ActivityDetail, ActivitySummary, ActivityZones, Sport, Zone } from '@painpal/contracts';
import { formatPace, thresholdSport, type ZoneModel } from '@painpal/core';
import { formatDayMonthYear, formatDuration, formatPaceKm } from '../../lib/format';
import { boundRange, hrModelFor, itemAt, zoneLabels } from '../../lib/zones';
import { ZONE_PALETTES } from '../../theme/tokens';

export interface ZoneRow { key: string; name: string; range: string; seconds: number; pct: number; widthPct: number; color: string }
export type ZoneGroupKey = 'power' | 'pace' | 'hr';
export interface ZoneGroup { key: ZoneGroupKey; title: string; threshold: string; total: string; rows: ZoneRow[] }

interface GroupSpec { key: ZoneGroupKey; title: string; thresholdLabel: string; context: string | null; unit: string; model: ZoneModel }

const sum = (values: readonly number[]): number => values.reduce((total, v) => total + v, 0);

export const zoneShare = (seconds: number, total: number): number => (total === 0 ? 0 : Math.round((seconds / total) * 100));

// Each section then checks its own data: power needs power, pace needs usable pace, HR needs HR.
export function showsAnalysis(activity: Pick<ActivitySummary, 'status'>): boolean {
  return activity.status === 'processed';
}

// Pace bounds descend (faster is lower); a pace at or under bound i is in zone i + 2.
export function paceRange(bounds: readonly number[], index: number): string {
  const slower = bounds[index - 1];
  const faster = bounds[index];
  if (faster === undefined) return `≤ ${formatPace(Number(slower))}`;
  if (slower === undefined) return `> ${formatPace(faster)}`;
  return `${formatPace(faster + 1)}–${formatPace(slower)}`;
}

export function zoneRows(zone: Zone, model: ZoneModel, unit: string): ZoneRow[] {
  const total = sum(zone.seconds);
  const longest = Math.max(1, ...zone.seconds);
  const names = zoneLabels(model);
  const palette = ZONE_PALETTES[model];
  return zone.seconds.map((seconds, i) => ({
    key: `${model}-${i}`,
    name: itemAt(names, i),
    range: model === 'pace' ? paceRange(zone.bounds, i) : boundRange(zone.bounds, i, unit),
    seconds,
    pct: zoneShare(seconds, total),
    widthPct: Math.round((seconds / longest) * 1000) / 10,
    color: itemAt(palette, i),
  }));
}

function zoneGroup(zone: Zone, spec: GroupSpec): ZoneGroup {
  const context = spec.context === null ? '' : `${spec.context} · `;
  return {
    key: spec.key,
    title: spec.title,
    threshold: `${spec.thresholdLabel} · ${context}SINCE ${formatDayMonthYear(zone.thresholdFrom)}`,
    total: formatDuration(sum(zone.seconds)),
    rows: zoneRows(zone, spec.model, spec.unit),
  };
}

function hrThresholdName(model: ZoneModel, sport: Sport): string {
  if (model === 'hr_max') return 'MAX HR';
  if (sport === 'ride') return 'LTHR';
  return sport === 'run' ? 'RUN THRESHOLD HR' : 'THRESHOLD HR';
}

function hrGroup(zone: Zone, sport: Sport): ZoneGroup {
  const model = hrModelFor(zone.seconds.length);
  const context = model === 'hr_lthr' && thresholdSport(sport) === 'other' ? 'OTHER SPORTS' : null;
  return zoneGroup(zone, { key: 'hr', title: 'HEART RATE', thresholdLabel: `${hrThresholdName(model, sport)} ${zone.threshold} BPM`, context, unit: 'BPM', model });
}

export function zoneGroups(zones: ActivityZones, sport: Sport = 'ride'): ZoneGroup[] {
  const groups: ZoneGroup[] = [];
  if (zones.power !== undefined) {
    groups.push(zoneGroup(zones.power, { key: 'power', title: 'POWER', thresholdLabel: `FTP ${zones.power.threshold} W`, context: null, unit: 'W', model: 'power' }));
  }
  if (zones.pace !== undefined) {
    groups.push(zoneGroup(zones.pace, {
      key: 'pace', title: 'PACE', thresholdLabel: `THRESHOLD ${formatPaceKm(zones.pace.threshold)}`, context: null, unit: '/KM', model: 'pace',
    }));
  }
  if (zones.hr !== undefined) groups.push(hrGroup(zones.hr, sport));
  return groups;
}

type ZoneSources = Pick<ActivityDetail, 'sport' | 'avgPower' | 'avgHr' | 'weightedSpeed'>;

// Rides zone power and runs zone pace beside HR; every other sport has HR zones only.
function sportReason(a: ZoneSources): string[] {
  if (a.sport === 'ride') return [a.avgPower === null ? 'NO POWER DATA' : 'NO FTP FOR THIS DATE'];
  if (a.sport === 'run') return [a.weightedSpeed === null ? 'NO PACE DATA' : 'NO THRESHOLD PACE FOR THIS DATE'];
  return [];
}

// Only shown when every group is absent, so a stream with data must be missing its threshold.
export function missingZonesReason(activity: ZoneSources): string {
  const hr = activity.avgHr === null ? 'NO HEART RATE DATA' : 'NO HR THRESHOLD FOR THIS DATE';
  return ['NO ZONES', ...sportReason(activity), hr].join(' · ');
}
